import { useRef, useState, useLayoutEffect, useCallback } from 'react';

export type UseDetectOverflowAxis = 'horizontal' | 'vertical' | 'both';

export type UseDetectOverflowOptions = {
  /** Which overflow direction to measure (default: horizontal, for single-line ellipsis) */
  axis?: UseDetectOverflowAxis;
};

/**
 * Hook to detect if the content of a given element overflows its bounds.
 */
export const useDetectOverflow = <T extends HTMLElement>(
  content?: string | number,
  options?: UseDetectOverflowOptions
) => {
  const ref = useRef<T>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const axis = options?.axis ?? 'horizontal';

  const checkOverflow = useCallback(() => {
    const refEl = ref.current;
    if (refEl) {
      const horizontal = refEl.scrollWidth > refEl.offsetWidth;
      const vertical = Math.ceil(refEl.scrollHeight) > Math.floor(refEl.clientHeight);
      const overflowed =
        (axis === 'horizontal' && horizontal) ||
        (axis === 'vertical' && vertical) ||
        (axis === 'both' && (horizontal || vertical));
      setIsOverflowed(overflowed);
    }
  }, [axis]);

  useLayoutEffect(() => {
    const refEl = ref.current;
    if (!refEl) return;

    checkOverflow();

    // Use ResizeObserver to detect size/content changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });
    resizeObserver.observe(refEl);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
    // Include `content` to re-measure if content or layout changes forcing observer to reset.
  }, [content, checkOverflow]);

  return { ref, isOverflowed };
};
